import { useUser } from "@clerk/clerk-react";
import { ArrowLeft, FileText, Trash2, Upload } from "lucide-react";
import type React from "react";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { apiClient } from "../lib/apiClient";

export const TermsAndConditionsPage: React.FC = () => {
	const { user, isLoaded } = useUser();
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [uploading, setUploading] = useState(false);
	const [uploadSuccess, setUploadSuccess] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			const allowedTypes = ["application/pdf"];
			if (!allowedTypes.includes(file.type)) {
				setError("Please select a valid PDF file");
				return;
			}

			// Validate file size (max 5MB to match backend validation)
			if (file.size > 5 * 1024 * 1024) {
				setError("File size must be less than 5MB");
				return;
			}

			setSelectedFile(file);
			setUploadSuccess(false);
			setError(null);
		}
	};

	const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
	};

	const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		const file = event.dataTransfer.files[0];
		if (file) {
			// Directly call the validation logic with a mock object to avoid type issues
			handleFileSelect({
				target: {
					files: [file],
				},
			} as unknown as React.ChangeEvent<HTMLInputElement>);
		}
	};
	const handleUpload = async () => {
		if (!selectedFile || !user?.id) {
			setError("Please select a file and ensure you are logged in.");
			return;
		}

		setUploading(true);
		setError(null);

		try {
			// Create FormData to send the file
			const formData = new FormData();
			formData.append("file", selectedFile);

			// Make API call to upload the terms and conditions document
			const response = await apiClient().post("/term/upload", formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			console.log("Upload successful:", response.data);
			setUploadSuccess(true);
			setSelectedFile(null);

			// Clear the file input
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		} catch (error: unknown) {
			console.error("Upload failed:", error);
			const apiError = error as { response?: { data?: { message?: string } } };
			const errorObj = error as Error;
			const errorMessage =
				apiError.response?.data?.message ||
				errorObj.message ||
				"Failed to upload the document. Please try again.";
			setError(errorMessage);
		} finally {
			setUploading(false);
		}
	};

	const removeFile = () => {
		setSelectedFile(null);
		setUploadSuccess(false);
		setError(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const formatFileSize = (bytes: number): string => {
		if (bytes === 0) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
	};

	// Show loading state if user is not loaded yet
	if (!isLoaded) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50 flex items-center justify-center">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50">
			<div className="max-w-2xl mx-auto sm:p-6 pt-6">
				<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
					{/* Header */}
					<div className="px-6 py-5 border-b border-gray-200">
						<div className="flex items-center mb-4">
							<Link
								to="/settings"
								className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
							>
								<ArrowLeft className="h-5 w-5 text-gray-600" />
							</Link>
							<div>
								<h2 className="text-2xl font-bold text-gray-900">Terms and Conditions</h2>
								<p className="mt-1 text-sm text-gray-500">
									Upload your terms and conditions document
								</p>
							</div>
						</div>
					</div>

					{/* Main Content */}
					<div className="px-6 py-6">
						{uploadSuccess && (
							<div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
								<div className="flex items-center">
									<FileText className="h-5 w-5 text-green-600 mr-2" />
									<p className="text-sm text-green-700">
										Terms and conditions document uploaded successfully!
									</p>
								</div>
							</div>
						)}

						{error && (
							<div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
								<div className="flex items-center">
									<div className="flex-shrink-0">
										<svg
											className="h-5 w-5 text-red-400"
											viewBox="0 0 20 20"
											fill="currentColor"
											role="img"
										>
											<title>Error icon</title>
											<path
												fillRule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
												clipRule="evenodd"
											/>
										</svg>
									</div>
									<p className="ml-2 text-sm text-red-700">
										Something went wrong while trying to upload, the team have been notified.
									</p>
								</div>
							</div>
						)}

						{/* Upload Area */}
						<div
							className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
								selectedFile
									? "border-gray-300 bg-gray-50"
									: "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
							}`}
							onDragOver={handleDragOver}
							onDrop={handleDrop}
						>
							<input
								ref={fileInputRef}
								type="file"
								accept=".pdf"
								onChange={handleFileSelect}
								className={`absolute inset-0 w-full h-full opacity-0 cursor-pointer ${
									selectedFile ? "pointer-events-none" : ""
								}`}
								disabled={uploading}
								aria-label="Upload terms and conditions PDF file"
							/>

							{selectedFile ? (
								<div className="space-y-4">
									<div className="flex items-center justify-center space-x-2">
										<FileText className="h-8 w-8 text-blue-600" />
									</div>
									<div>
										<p className="text-lg font-medium text-gray-900">{selectedFile.name}</p>
										<p className="text-sm text-gray-500">{formatFileSize(selectedFile.size)}</p>
									</div>
									<div className="flex flex-col gap-4 justify-center md:flex-row">
										<Button
											onClick={handleUpload}
											disabled={uploading}
											className="bg-gradient-to-r from-orange-400 to-pink-400 hover:from-orange-500 hover:to-pink-500 disabled:from-gray-300 disabled:to-gray-300 text-white font-semibold transition-all duration-300 shadow-lg"
										>
											{uploading ? (
												<>
													<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
													Uploading...
												</>
											) : (
												<>
													<Upload className="h-4 w-4" />
													Upload Document
												</>
											)}
										</Button>
										<Button onClick={removeFile} variant="outline" disabled={uploading}>
											<Trash2 className="h-4 w-4" />
											Remove
										</Button>
									</div>
								</div>
							) : (
								<div className="space-y-4">
									<div className="flex items-center justify-center space-x-2">
										<Upload className="h-8 w-8 text-gray-400" />
									</div>
									<div>
										<p className="text-lg font-medium text-gray-900">
											Drop your terms and conditions document here
										</p>
										<p className="text-sm text-gray-500">or click to browse files</p>
									</div>
									<div className="text-xs text-gray-400">
										<p>Supported formats: PDF only</p>
										<p>Maximum file size: 5MB</p>
									</div>
								</div>
							)}
						</div>

						{/* Instructions */}
						<div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
							<h3 className="text-sm font-medium text-blue-900 mb-2">Important Information</h3>
							<ul className="text-sm text-blue-800 space-y-1">
								<li>• The uploaded document will replace any existing terms and conditions</li>
								<li>• Make sure your document is properly formatted and legally reviewed</li>
								<li>• Users will be required to accept the new terms before making payments</li>
								<li>• You can upload a new version at any time</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
